import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_BASE_URL } from "../config";
import { setError } from "./errorSlice"; // Optional if you want to show global errors

export const initialState = {
  classRoomAttendance: [],
  homeRoomAttendance: [],
  status: "idle",
  error: null,
  loading: false,
  success: false,
};

// Helper function to extract token
const getToken = (dispatch, rejectWithValue) => {
  const storedUser = localStorage.getItem("userInfo");

  if (!storedUser) {
    const errorMsg = "Session expired. Please log in again.";
    dispatch?.(setError(errorMsg));
    return rejectWithValue(errorMsg);
  }

  const { token } = JSON.parse(storedUser);

  if (!token) {
    const errorMsg = "Session expired. Please log in again.";
    dispatch?.(setError(errorMsg));
    return rejectWithValue(errorMsg);
  }

  return token;
};

// Fetch Classroom Attendance
export const fetchClassRoomAttendance = createAsyncThunk(
  "attendance/fetchClassRoomAttendance",
  async (studentId, { rejectWithValue, dispatch }) => {
    try {
      const token = getToken(dispatch, rejectWithValue);
      if (typeof token !== "string") return token;

      const response = await fetch(
        `${API_BASE_URL}/api/Attendance/ClassRoomAttendance/student/${studentId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error("Expected classroom attendance data to be an array");
      }

      return data;
    } catch (error) {
      console.error("Error fetching classroom attendance:", error);
      dispatch(setError(error.message));
      return rejectWithValue(error.message || "Failed to fetch classroom attendance");
    }
  }
);

// Fetch HomeRoom Attendance
export const fetchHomeRoomAttendance = createAsyncThunk(
  "attendance/fetchHomeRoomAttendance",
  async (studentId, { rejectWithValue, dispatch }) => {
    try {
      const token = getToken(dispatch, rejectWithValue);
      if (typeof token !== "string") return token;

      const response = await fetch(
        `${API_BASE_URL}/api/Attendance/HomeRoomStudentAttendance/student/${studentId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error("Expected homeroom attendance data to be an array");
      }

      return data;
    } catch (error) {
      console.error("Error fetching homeroom attendance:", error);
      dispatch(setError(error.message));
      return rejectWithValue(error.message || "Failed to fetch homeroom attendance");
    }
  }
);

const attendanceSlice = createSlice({
  name: "attendance",
  initialState,
  reducers: {
    clearAttendanceError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ClassRoom Attendance
      .addCase(fetchClassRoomAttendance.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClassRoomAttendance.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.classRoomAttendance = action.payload;
      })
      .addCase(fetchClassRoomAttendance.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload;
      })

      // HomeRoom Attendance
      .addCase(fetchHomeRoomAttendance.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHomeRoomAttendance.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.homeRoomAttendance = action.payload;
      })
      .addCase(fetchHomeRoomAttendance.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearAttendanceError } = attendanceSlice.actions;
export default attendanceSlice.reducer;
